import { computed, inject, onBeforeUnmount, onMounted, provide, reactive, toRef, watch } from 'vue';

import { useProxyModel } from '@components/composable';
import {
  deepEqual,
  findChildrenWithProvide,
  getCurrentInstance,
  getUid,
  propsFactory,
  warn,
  wrapInArray
} from '@components/utils';

import type { IDisabledProps } from '@components/composable';
import type {
  ComponentInternalInstance,
  ComputedRef,
  ExtractPropTypes,
  InjectionKey,
  PropType,
  Ref,
  UnwrapRef
} from 'vue';

export interface IGroupItem {
  id: number;
  value: Ref<unknown>;
  disabled: Ref<boolean | undefined>;
}

export interface IGroupProps {
  disabled?: boolean;
  modelValue: unknown;
  multiple?: boolean;
  required?: boolean | 'force';
  max?: number;
  selectedClass: string | undefined;
  'onUpdate:modelValue'?: ((val: unknown) => void) | undefined;
}

export interface IGroupProvide {
  selected: Ref<Readonly<number[]>>;
  selectedClass: Ref<string | undefined>;
  items: ComputedRef<
    {
      id: number;
      value: unknown;
      disabled: boolean | undefined;
    }[]
  >;
  disabled: Ref<boolean | undefined>;
  register: (item: IGroupItem, instance: ComponentInternalInstance) => void;
  unregister: (id: number) => void;
  select: (id: number, value: boolean) => void;
  isSelected: (id: number) => boolean;
  prev: () => void;
  next: () => void;
  getItemIndex: (value: unknown) => number;
}

export interface IGroupItemProvide {
  id: number;
  isSelected: Ref<boolean>;
  selectedClass: Ref<(string | undefined)[] | false>;
  value: Ref<unknown>;
  disabled: Ref<boolean | undefined>;
  group: IGroupProvide;
  select: (value: boolean) => void;
  toggle: () => void;
}

export const makeGroupProps = propsFactory(
  {
    /**
     * Значение v-model
     */
    modelValue: {
      type: null,
      default: undefined
    },
    /**
     * Множественный выбор
     */
    multiple: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Обязательно должен быть выбран хотя бы один элемент
     */
    required: {
      type: [Boolean, String] as PropType<boolean | 'force'>,
      default: undefined
    },
    /**
     * Максимально допустимое количество выбранных элементов
     */
    max: {
      type: Number as PropType<number>,
      default: undefined
    },
    /**
     * Классы для выбранных элементов
     */
    selectedClass: {
      type: String as PropType<string>,
      default: undefined
    }
  },
  'group'
);

export interface IGroupItemProps extends ExtractPropTypes<ReturnType<typeof makeGroupItemProps>> {
  disabled: IDisabledProps['disabled'];
  'onGroup:selected': ((value: { value: boolean }) => void) | undefined;
}

export const makeGroupItemProps = propsFactory(
  {
    /**
     * Значение элемента
     */
    value: {
      type: null,
      default: undefined
    },
    /**
     * Классы для выбранных элементов
     */
    selectedClass: {
      type: String as PropType<string>,
      default: undefined
    }
  },
  'group-item'
);

export interface ISlideGroupProps extends ExtractPropTypes<ReturnType<typeof makeSlideGroupProps>> {
  disabled: IDisabledProps['disabled'];
  'onGroup:selected': ((value: { value: boolean }) => void) | undefined;
}

export const makeSlideGroupProps = propsFactory(
  {
    /**
     * Принудительно центрировать активный элемент
     */
    centerActive: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Показывать стрелки для переключения между элементами
     */
    showArrows: {
      type: [Boolean, String] as PropType<boolean | string>,
      validator: (value: any) => typeof value === 'boolean' || ['always', 'desktop', 'mobile'].includes(value)
    }
  },
  'slide-group'
);

export function useGroupItem(
  props: IGroupItemProps,
  injectKey: InjectionKey<IGroupProvide>,
  required?: true
): IGroupItemProvide;
export function useGroupItem(
  props: IGroupItemProps,
  injectKey: InjectionKey<IGroupProvide>,
  required: false
): IGroupItemProvide | null;
export function useGroupItem(
  props: IGroupItemProps,
  injectKey: InjectionKey<IGroupProvide>,
  required = true
): IGroupItemProvide | null {
  const vm = getCurrentInstance('useGroupItem');

  if (!vm) {
    throw new Error('[CDS] useGroupItem composable must be used inside a component setup function.');
  }

  const id = getUid();

  provide(Symbol.for(`${injectKey.description}:id`), id);

  const group = inject(injectKey, null);

  if (!group) {
    if (!required) return group;

    throw new Error(`[CDS] Could not find useGroup injection with symbol ${injectKey.description}`);
  }

  const value = toRef(props, 'value');
  const disabled = computed(() => group.disabled.value || props.disabled);

  group.register({ id, value, disabled }, vm);

  onBeforeUnmount(() => {
    group.unregister(id);
  });

  const isSelected = computed(() => {
    return group.isSelected(id);
  });
  const selectedClass = computed(() => isSelected.value && [group.selectedClass.value, props.selectedClass]);

  watch(isSelected, (value) => {
    vm.emit('group:selected', { value });
  });

  return {
    id,
    isSelected,
    toggle: () => group.select(id, !isSelected.value),
    select: (value: boolean) => group.select(id, value),
    selectedClass,
    value,
    disabled,
    group
  };
}

export function useGroup(props: IGroupProps, injectKey: InjectionKey<IGroupProvide>): IGroupProvide {
  let isUnmounted = false;

  const items = reactive<IGroupItem[]>([]);
  const selected = useProxyModel(
    props,
    'modelValue',
    [],
    (value) => {
      if (value == null) return [];
      return getIds(items, wrapInArray(value));
    },
    (value) => {
      const arr = getValues(items, value);
      return props.multiple ? arr : arr[0];
    }
  );

  const groupVm = getCurrentInstance('useGroup');

  function register(item: IGroupItem, vm: ComponentInternalInstance) {
    const unwrapped = item as unknown as UnwrapRef<IGroupItem>;
    const key = Symbol.for(`${injectKey.description}:id`);
    const children = findChildrenWithProvide(key, groupVm?.vnode);
    const index = children.indexOf(vm);

    if (index > -1) {
      items.splice(index, 0, unwrapped);
    } else {
      items.push(unwrapped);
    }
  }

  function unregister(id: number) {
    if (isUnmounted) return;

    forceRequiredValue();

    const index = items.findIndex((item) => item.id === id);
    items.splice(index, 1);
  }

  // If required and nothing is selected, then select first non-disabled item
  function forceRequiredValue() {
    const item = items.find((item) => !item.disabled);
    if (item && props.required === 'force' && !selected.value.length) {
      selected.value = [item.id];
    }
  }

  onMounted(() => {
    forceRequiredValue();
  });

  onBeforeUnmount(() => {
    isUnmounted = true;
  });

  function select(id: number, value?: boolean) {
    const item = items.find((item) => item.id === id);

    if (value && item?.disabled) return;

    if (props.multiple) {
      const internalValue = selected.value.slice();
      const index = internalValue.findIndex((value) => value === id);
      const isSelected = ~index;

      value = value ?? !isSelected;

      if (isSelected && props.required && internalValue.length <= 1) return;
      if (!isSelected && props.max != null && internalValue.length + 1 > props.max) return;

      if (index < 0 && value) {
        internalValue.push(id);
      } else if (index >= 0 && !value) {
        internalValue.splice(index, 1);
      }

      selected.value = internalValue;
    } else {
      const isSelected = selected.value.includes(id);
      if (props.required && isSelected) return;

      selected.value = value ?? !isSelected ? [id] : [];
    }
  }

  function step(offset: number) {
    if (props.multiple) {
      warn('Group', 'step is not supported when using multiple selection');
    }

    if (!selected.value.length) {
      const item = items.find((item) => !item.disabled);
      item && (selected.value = [item.id]);
    } else {
      const currentId = selected.value[0];
      const currentIndex = items.findIndex((i) => i.id === currentId);

      let newIndex = (currentIndex + offset) % items.length;
      let newItem = items[newIndex];

      while (newItem.disabled && newIndex !== currentIndex) {
        newIndex = (newIndex + offset) % items.length;
        newItem = items[newIndex];
      }

      if (newItem.disabled) return;

      selected.value = [items[newIndex].id];
    }
  }

  const state: IGroupProvide = {
    register,
    unregister,
    select,
    selected,
    disabled: toRef(props, 'disabled'),
    prev: () => step(items.length - 1),
    next: () => step(1),
    isSelected: (id: number) => selected.value.includes(id),
    selectedClass: computed(() => props.selectedClass),
    items: computed(() => items),
    getItemIndex: (value: unknown) => getItemIndex(items, value)
  };

  provide(injectKey, state);

  return state;
}

function getItemIndex(items: UnwrapRef<IGroupItem[]>, value: unknown) {
  const ids = getIds(items, [value]);

  if (!ids.length) return -1;

  return items.findIndex((item) => item.id === ids[0]);
}

function getIds(items: UnwrapRef<IGroupItem[]>, modelValue: any[]) {
  const ids: number[] = [];

  modelValue.forEach((value) => {
    const item = items.find((item) => deepEqual(value, item.value));
    const itemByIndex = items[value];

    if (item?.value != null) {
      ids.push(item.id);
    } else if (itemByIndex != null) {
      ids.push(itemByIndex.id);
    }
  });

  return ids;
}

function getValues(items: UnwrapRef<IGroupItem[]>, ids: any[]) {
  const values: unknown[] = [];

  ids.forEach((id) => {
    const itemIndex = items.findIndex((item) => item.id === id);

    if (~itemIndex) {
      const item = items[itemIndex];
      values.push(item.value != null ? item.value : itemIndex);
    }
  });

  return values;
}
